import {
  isEnableDemoFeatures,
  DOCUMENT_TYPE_VGM_DETAILS,
  BL_DOC_PRIV_LOI_OT,
  BL_DOC_PRIV_LOI_BL,
  BL_DOC_PRIV_OBL_DL,
  BL_DOC_PRIV_SWB_DL,
  BL_DOC_PRIV_DE,
} from '../../../utils/CTUtils';
import { hasOcpCognitoAccess } from '../../../utils/ocpRequestUtils';
import { getCognitoGroups } from './headerProfileGroups';

function inGroup(grps, code) {
  return grps.includes(code);
}

export const DOM_BILLING_DOC_PERMISSIONS = [
  'IKEBL',
  'IKEDO',
  'IKEDR',
  'IKEFB',
  'IKEFC',
  'IKEFP',
  'IKENA',
  'IKENB',
  'IKENN',
  'IKESW',
];

/**
 * Cognito group flags and nav visibility for the domestic logged-in header and hubs.
 */
export function getDomesticNavAccess(grps, user) {
  const hasBD = inGroup(grps, 'BD');
  const hasSI = inGroup(grps, 'SI') || inGroup(grps, 'BS');
  const hasBC = inGroup(grps, 'BC') || inGroup(grps, 'BP');
  const hasBR = inGroup(grps, 'BR') || inGroup(grps, 'BS');
  const hasOcp = hasOcpCognitoAccess(grps) && isEnableDemoFeatures();
  const hasIkebv = inGroup(grps, 'IKEBV');
  const hasIkebm = inGroup(grps, 'IKEBM');
  const hasIntlSI = hasSI;
  const hasDomSI = inGroup(grps, 'IKEBI');
  const hasIntlBooking = hasBD || hasBR || hasBC || hasOcp;
  const hasDomBooking = hasIkebv || hasIkebm;
  const hasBooking = hasIntlBooking || hasDomBooking;
  const intlBookingPath = hasBD
    ? '/bookingdash'
    : hasBR
    ? '/bookingRequest'
    : hasOcp
    ? '/ocpRequest'
    : '/bookingUpdate';
  const hasVS = inGroup(grps, 'VS');
  const hasRep = inGroup(grps, 'VA') || inGroup(grps, 'VU');
  const billingGroups =
    inGroup(grps, BL_DOC_PRIV_LOI_OT) ||
    inGroup(grps, BL_DOC_PRIV_LOI_BL) ||
    inGroup(grps, BL_DOC_PRIV_SWB_DL) ||
    inGroup(grps, BL_DOC_PRIV_OBL_DL);
  const hasBlanket = inGroup(grps, BL_DOC_PRIV_LOI_BL);
  const hasVgm = inGroup(grps, DOCUMENT_TYPE_VGM_DETAILS);
  const hasDetention =
    inGroup(grps, BL_DOC_PRIV_DE) &&
    (user.continent_code === 'AS' ||
      import.meta.env.REACT_APP_STAGE !== 'production');
  const hasIntlShippingInstruction = hasIntlSI || hasDetention;
  const hasShippingInstruction = hasIntlShippingInstruction || hasDomSI;
  const intlShippingInstructionPath = hasIntlSI
    ? '/shippingInstructions'
    : '/detentionChargeCalculator';
  const hasIntlDocument = billingGroups || hasBlanket;
  const hasDomDocument = DOM_BILLING_DOC_PERMISSIONS.some((code) =>
    inGroup(grps, code)
  );
  const hasDocument = hasIntlDocument || hasDomDocument;
  const intlDocumentPath = billingGroups ? '/billingdoc' : '/blanketloi';
  const hasCT = inGroup(grps, 'CT');
  const hasTD = inGroup(grps, 'TD');
  const hasIkest = inGroup(grps, 'IKEST');
  const hasIketa = inGroup(grps, 'IKETA');

  let showContainerSearch = true;
  if (
    import.meta.env.REACT_APP_COUNTRY_CODE === 'US' &&
    inGroup(grps, DOCUMENT_TYPE_VGM_DETAILS) &&
    !inGroup(grps, 'BD')
  ) {
    showContainerSearch = false;
  }

  // Intl Shipment Tracking = TD; Domestic = IKEST/IKETA. Container Search is
  // placed separately: main bar when neither, else under each granted submenu.
  const hasIntlShipment = hasTD;
  const hasDomShipment = hasIkest || hasIketa;
  const hasShipmentTracking = hasIntlShipment || hasDomShipment;
  const showContainerSearchInMainNav =
    showContainerSearch && !hasIntlShipment && !hasDomShipment;
  const useDomesticIntlContainerUi = inGroup(grps, 'DOMESTIC');
  const intlContainerSearchPath = useDomesticIntlContainerUi
    ? '/d/intl/search'
    : '/search';
  const intlShipmentPath = hasTD ? '/containerdash' : intlContainerSearchPath;
  const showDocMenu = hasDocument;
  // Domestic Account Balances (CBPR-1952): MATNAV role "ar" maps to IKEAR.
  const hasDomAccountStatement = inGroup(grps, 'IKEAR');
  const hasAccountStatement = hasDomAccountStatement;
  const accountStatementPath = '/d/account-statement/balances';
  const canViewDomBooking = hasIkebv || hasIkebm;

  return {
    hasBD,
    hasSI,
    hasIntlSI,
    hasDomSI,
    hasIntlShippingInstruction,
    hasShippingInstruction,
    intlShippingInstructionPath,
    hasIntlDocument,
    hasDomDocument,
    hasDocument,
    intlDocumentPath,
    hasBC,
    hasBR,
    hasOcp,
    hasIkebv,
    hasIkebm,
    hasIntlBooking,
    hasDomBooking,
    hasBooking,
    intlBookingPath,
    hasVS,
    hasRep,
    billingGroups,
    hasBlanket,
    hasVgm,
    hasDetention,
    hasCT,
    hasTD,
    hasIkest,
    hasIketa,
    showContainerSearch,
    showContainerSearchInMainNav,
    useDomesticIntlContainerUi,
    intlContainerSearchPath,
    hasIntlShipment,
    hasDomShipment,
    hasShipmentTracking,
    intlShipmentPath,
    showDocMenu,
    hasDomAccountStatement,
    hasAccountStatement,
    accountStatementPath,
    canViewDomBooking,
  };
}

/** Default hub landing path; order matches domestic booking sub-bar. */
export function defaultDomesticBookingPath(grps) {
  if (grps.includes('IKEBM')) return '/d/booking/create';
  if (grps.includes('IKEBV')) return '/d/booking/view';
  return '/d/booking/create';
}

/** Default hub landing path; order matches domestic shipment sub-bar. */
export function defaultDomesticShipmentPath(grps) {
  if (grps.includes('IKEST')) return '/d/shipment/tracking';
  if (grps.includes('IKETA')) return '/d/shipment/autos';
  return '/d/shipment/tracking';
}

/**
 * Booking # on Booking Dashboard deep-links to /d/shipment/booking.
 * IKEST users already have that page; IKEBM/IKEBV (dashboard) must as well.
 */
export function canAccessDomesticShipmentBookingDetail(access) {
  return Boolean(access?.hasIkest || access?.canViewDomBooking);
}

/**
 * Booking-dashboard-only users may enter the shipment hub for booking detail,
 * but other /d/shipment routes still fall back to the booking dashboard.
 */
export function resolveDomesticShipmentHubFallbackPath(
  access,
  shipmentInitialPath
) {
  if (access?.hasDomShipment) return shipmentInitialPath;
  if (access?.canViewDomBooking) return '/d/booking/dashboard';
  return shipmentInitialPath;
}

export const DOMESTIC_HOME_PATH = '/d/home';

/**
 * True when the groups grant any international page (not the DOMESTIC marker).
 * Container Tracking (CT) is international even though it does not open the
 * TD shipment menu.
 */
export function hasInternationalAccess(access) {
  if (!access) return false;
  return Boolean(
    access.hasIntlShipment ||
      access.hasIntlBooking ||
      access.hasIntlShippingInstruction ||
      access.hasIntlDocument ||
      access.hasVgm ||
      access.hasVS ||
      access.hasRep ||
      access.hasCT
  );
}

/** True when the groups grant any domestic page. */
export function hasDomesticAccess(access) {
  if (!access) return false;
  return Boolean(
    access.hasDomShipment ||
      access.hasDomBooking ||
      access.hasDomSI ||
      access.hasDomDocument ||
      access.hasDomAccountStatement
  );
}

/**
 * Groups that decide domestic vs international persona: the signed-in user's
 * own token when there is one, otherwise the groups passed in (login, before
 * a user object exists). Never the assumed user's groups.
 */
export function ownGroupsOf(user, grps = []) {
  if (user && user.idToken) return getCognitoGroups(user) || [];
  return Array.isArray(grps) ? grps : [];
}

/** Domestic CSR is the IKECSR permission on the signed-in user. */
export function isDomesticCsr(user, grps = []) {
  return ownGroupsOf(user, grps).includes('IKECSR');
}

/**
 * Domestic user: no ADMIN, CSR, or IKECSR, zero international permissions,
 * and at least one domestic permission.
 */
export function isDomesticCustomer(user, grps = []) {
  const groups = ownGroupsOf(user, grps);
  if (
    groups.includes('ADMIN') ||
    groups.includes('CSR') ||
    groups.includes('IKECSR')
  ) {
    return false;
  }
  const access = getDomesticNavAccess(groups, user || {});
  return !hasInternationalAccess(access) && hasDomesticAccess(access);
}

export function canOpenDomesticHome(user, grps = []) {
  return isDomesticCsr(user, grps) || isDomesticCustomer(user, grps);
}

/**
 * Header name. Domestic customers and domestic CSRs see their name.
 * International users keep the sign-in email.
 */
export function personaDisplayName(user, fullName) {
  const emailName = (user && user.username) || '';
  if ((isDomesticCustomer(user) || isDomesticCsr(user)) && fullName) {
    return fullName;
  }
  return emailName;
}

/** USER PROFILE column stays for current admin users of Admin User Search. */
export function showsAdminUserProfileColumn(user) {
  return (getCognitoGroups(user) || []).includes('ADMIN');
}

/** Admins, plus domestic CSRs. International CSRs stay out. */
export function canSearchAdminAccounts(user) {
  return showsAdminUserProfileColumn(user) || isDomesticCsr(user);
}

/**
 * Default post-login / permission-fallback landing when routing to
 * "container search". Prefers international container search when that
 * section is available (main nav or intl shipment submenu); otherwise
 * domestic shipment. DOMESTIC group uses `/d/intl/search` for intl search.
 * With demo features on, domestic customers and IKECSR users land on /d/home.
 */
export function defaultContainerSearchLandingPath(grps = [], user = {}) {
  if (isEnableDemoFeatures() && canOpenDomesticHome(user, grps)) {
    return DOMESTIC_HOME_PATH;
  }
  const access = getDomesticNavAccess(grps, user);
  const {
    showContainerSearch,
    hasIntlShipment,
    hasDomShipment,
    intlContainerSearchPath,
  } = access;

  // Intl Container Search appears in main nav when neither shipment area is
  // granted, or under the international shipment submenu when intl is granted.
  // Domestic-only users only see domestic search under /d/shipment.
  const hasIntlContainerSearch =
    showContainerSearch && (!hasDomShipment || hasIntlShipment);

  if (!hasIntlContainerSearch && hasDomShipment) {
    return defaultDomesticShipmentPath(grps);
  }

  return intlContainerSearchPath || '/search';
}

function detailModeToSubNavKey(location) {
  if (!location) return 'create';
  const params = new URLSearchParams(location.search || '');
  const mode = params.get('mode');
  switch (mode) {
    case 'VIEW': return 'view';
    case 'UPDATE': return 'update';
    case 'CANCEL': return 'cancel';
    default: return 'create';
  }
}

function domBookingSubActive(pathname, key, location) {
  const onBookingDetails = pathname.includes('/bookingRequestDetails');
  if (onBookingDetails) {
    return key === detailModeToSubNavKey(location);
  }
  if (pathname.includes('/viewMyTemplate')) {
    return key === 'create';
  }
  if (pathname.startsWith('/d/shipment/booking')) {
    return key === 'dashboard';
  }
  return pathname.includes(`/d/booking/${key}`);
}

function buildSubLink({ to, icon, labelKey, isActive, visible = true, reselect }) {
  if (!visible) return null;
  return reselect ? { to, icon, labelKey, isActive, reselect } : { to, icon, labelKey, isActive };
}

export function buildIntlBookingSubLinks(access, pathname) {
  const { hasBD, hasBR, hasBC, hasOcp } = access;
  return [
    buildSubLink({
      to: '/bookingdash',
      icon: 'booking-dashboard',
      labelKey: 'header.bookingDashboard',
      visible: hasBD,
      isActive: pathname.startsWith('/bookingdash'),
    }),
    buildSubLink({
      to: '/bookingRequest',
      icon: 'create-booking',
      labelKey: 'header.bookingRequest',
      visible: hasBR,
      isActive: pathname.startsWith('/bookingRequest'),
    }),
    buildSubLink({
      to: '/ocpRequest',
      icon: 'create-booking',
      labelKey: 'header.ocpRequest',
      visible: hasOcp,
      isActive:
        pathname.startsWith('/ocpRequest') ||
        pathname.startsWith('/ocpRequestDetails'),
    }),
    buildSubLink({
      to: '/bookingUpdate',
      icon: 'update-booking',
      labelKey: 'header.bookingCorrection',
      visible: hasBC,
      isActive: pathname.startsWith('/bookingUpdate'),
    }),
  ].filter(Boolean);
}

export function buildDomBookingSubLinks(access, pathname, location) {
  const { hasIkebm, hasIkebv } = access;
  const subActive = (key) => domBookingSubActive(pathname, key, location);
  return [
    buildSubLink({
      to: '/d/booking/create',
      icon: 'create-booking',
      labelKey: 'header.createBooking',
      visible: hasIkebm,
      isActive: subActive('create'),
    }),
    buildSubLink({
      to: '/d/booking/view',
      icon: 'view-booking',
      labelKey: 'header.viewBooking',
      visible: hasIkebv || hasIkebm,
      isActive: subActive('view'),
    }),
    buildSubLink({
      to: '/d/booking/update',
      icon: 'update-booking',
      labelKey: 'header.updateBooking',
      visible: hasIkebm,
      isActive: subActive('update'),
    }),
    buildSubLink({
      to: '/d/booking/cancel',
      icon: 'cancel-booking',
      labelKey: 'header.cancelBooking',
      visible: hasIkebm,
      isActive: subActive('cancel'),
    }),
    buildSubLink({
      to: '/d/booking/dashboard',
      icon: 'booking-dashboard',
      labelKey: 'header.bookingDashboard',
      visible: hasIkebv || hasIkebm,
      isActive: subActive('dashboard'),
    }),
    buildSubLink({
      to: '/d/booking/templates',
      icon: 'manage-templates',
      labelKey: 'header.manageMyTemplates',
      visible: hasIkebm,
      isActive: subActive('templates'),
    }),
    buildSubLink({
      to: '/d/booking/details',
      icon: 'booking-details',
      labelKey: 'header.bookingDetails',
      visible: hasIkebv || hasIkebm,
      isActive: subActive('details'),
    }),
  ].filter(Boolean);
}

export function buildIntlShipmentSubLinks(access, pathname) {
  const {
    showContainerSearch,
    hasTD,
    useDomesticIntlContainerUi,
    intlContainerSearchPath,
  } = access;
  const searchActive = useDomesticIntlContainerUi
    ? pathname.startsWith('/d/intl')
    : pathname.startsWith('/search') || pathname.startsWith('/containerList');
  return [
    buildSubLink({
      to: '/containerdash',
      icon: 'tracking-dashboard',
      labelKey: 'header.conatinerDashbaord',
      visible: hasTD,
      isActive: pathname.startsWith('/containerdash'),
    }),
    buildSubLink({
      to: intlContainerSearchPath || '/search',
      icon: 'container-search',
      labelKey: 'header.containerSearch',
      visible: showContainerSearch,
      isActive: searchActive,
    }),
  ].filter(Boolean);
}

export function buildDomShipmentSubLinks(access, pathname) {
  const { hasIkest, hasIketa, showContainerSearch } = access;
  return [
    buildSubLink({
      to: '/d/shipment/tracking',
      icon: 'track-container',
      labelKey: 'header.trackContainer',
      visible: hasIkest,
      isActive: pathname.includes('/d/shipment/tracking'),
      reselect: true,
    }),
    buildSubLink({
      to: '/d/shipment/dashboard',
      icon: 'tracking-dashboard',
      labelKey: 'header.containerDashboard',
      visible: hasIkest,
      isActive: pathname.includes('/d/shipment/dashboard'),
    }),
    buildSubLink({
      to: '/d/shipment/search',
      icon: 'container-search',
      labelKey: 'header.containerSearch',
      visible: showContainerSearch,
      isActive:
        pathname.includes('/d/shipment/search') ||
        pathname.includes('/d/shipment/list'),
    }),
    buildSubLink({
      to: '/d/shipment/autos',
      icon: 'track-autos',
      labelKey: 'header.trackAutos',
      visible: hasIketa,
      isActive: pathname.includes('/d/shipment/autos'),
    }),
  ].filter(Boolean);
}

export function defaultDomesticShippingInstructionPath() {
  return '/d/shipping-instruction';
}

export function buildDomShippingInstructionSubLinks(access, pathname) {
  const { hasDomSI } = access;
  return [
    buildSubLink({
      to: '/d/shipping-instruction',
      icon: 'shipping-instruction',
      labelKey: 'header.billingInstructions',
      visible: hasDomSI,
      isActive:
        pathname === '/d/shipping-instruction' ||
        pathname === '/d/shipping-instruction/',
    }),
    buildSubLink({
      to: '/d/shipping-instruction/create',
      icon: 'create-booking',
      labelKey: 'header.createBillingInstruction',
      visible: hasDomSI,
      isActive: pathname.startsWith('/d/shipping-instruction/create'),
    }),
    buildSubLink({
      to: '/d/shipping-instruction/view',
      icon: 'view-booking',
      labelKey: 'header.viewBillingInstruction',
      visible: hasDomSI,
      isActive:
        pathname.startsWith('/d/shipping-instruction/view') ||
        pathname.startsWith('/d/shipping-instruction/edit'),
    }),
  ].filter(Boolean);
}

export function buildIntlShippingInstructionSubLinks(access, pathname) {
  const { hasIntlSI, hasDetention } = access;
  return [
    buildSubLink({
      to: '/shippingInstructions',
      icon: 'shipping-instruction',
      labelKey: 'header.shippingInstruction',
      visible: hasIntlSI,
      isActive:
        pathname.startsWith('/shippingInstructions') ||
        pathname.startsWith('/shippingInstructionCompare'),
    }),
    buildSubLink({
      to: '/detentionChargeCalculator',
      icon: 'fa-calculator',
      labelKey: 'header.detention',
      visible: hasDetention,
      isActive: pathname.startsWith('/detentionChargeCalculator'),
    }),
  ].filter(Boolean);
}

export function buildIntlDocumentSubLinks(access, pathname) {
  const { billingGroups, hasBlanket } = access;
  return [
    buildSubLink({
      to: '/billingdoc',
      icon: 'billing-document',
      labelKey: 'header.billingDashboard',
      visible: billingGroups,
      isActive: pathname.startsWith('/billingdoc'),
    }),
    buildSubLink({
      to: '/blanketloi',
      icon: 'blanket-loi',
      labelKey: 'header.BlanketLOI',
      visible: hasBlanket,
      isActive:
        pathname.startsWith('/blanketloi') ||
        pathname.startsWith('/blanketloidetails'),
    }),
  ].filter(Boolean);
}

export function buildDomDocumentSubLinks(access, pathname) {
  return [
    buildSubLink({
      to: '/d/billingdocs',
      icon: 'billing-document',
      labelKey: 'header.billingDashboard',
      visible: access.hasDomDocument,
      isActive: pathname.startsWith('/d/billingdocs'),
    }),
  ].filter(Boolean);
}

export function buildDomAccountStatementSubLinks(access, pathname) {
  const { hasDomAccountStatement } = access;
  return [
    buildSubLink({
      to: '/d/account-statement/balances',
      icon: 'fa-file-text-o',
      labelKey: 'header.accountBalances',
      visible: hasDomAccountStatement,
      isActive: pathname.startsWith('/d/account-statement/balances'),
    }),
  ].filter(Boolean);
}

/**
 * Legacy pages linked from the domestic header's international menu → title
 * i18n key. Detail-only routes (e.g. /bookingRequestDetails, /containerDetails)
 * are intentionally omitted so they do not get a title bar.
 *
 * Detail-only routes (e.g. /bookingRequestDetails, /containerDetails,
 * /shippingInstructionsDetails, /blanketloidetails) are intentionally omitted.
 */
const LEGACY_PAGE_TITLE_KEYS = {
  '/containerdash': 'header.conatinerDashbaord',
  '/bookingdash': 'header.bookingDashboard',
  '/bookingRequest': 'header.bookingRequest',
  '/bookingUpdate': 'header.bookingCorrection',
  '/shippingInstructions': 'header.shippingInstruction',
  '/detentionChargeCalculator': 'header.detention',
  '/vgmDetails': 'header.vgmDetails',
  '/billingdoc': 'header.billingDashboard',
  '/blanketloi': 'header.BlanketLOI',
  '/salesView': 'header.salesView',
  '/ocpRequest': 'header.ocpRequest',
  '/containerList': 'containerList.txtTitle',
  '/repSalesView': 'header.repSalesViewMenu',
};

function matchesLegacyTitlePath(pathname, prefix) {
  return (
    pathname === prefix ||
    pathname.startsWith(`${prefix}/`) ||
    pathname.startsWith(`${prefix}:`)
  );
}

/**
 * i18n key for the title shown above a legacy page (rendered under the domestic
 * menu). Returns null for domestic (/d/) routes and pages not on the intl menu.
 */
export function getLegacyPageTitleKey(pathname) {
  if (!pathname || pathname.startsWith('/d/')) return null;
  const match = Object.keys(LEGACY_PAGE_TITLE_KEYS)
    .filter((prefix) => matchesLegacyTitlePath(pathname, prefix))
    .sort((a, b) => b.length - a.length)[0];
  return match ? LEGACY_PAGE_TITLE_KEYS[match] : null;
}

export function getDomesticHeaderSubNavState(pathname, access) {
  const showIntlBookingSub =
    pathname.startsWith('/bookingdash') ||
    pathname.startsWith('/bookingRequest') ||
    pathname.startsWith('/bookingUpdate') ||
    pathname.startsWith('/ocpRequest');
  const onShipmentBookingDetail = pathname.startsWith('/d/shipment/booking');
  const showDomBookingSub =
    pathname.startsWith('/d/booking') ||
    (onShipmentBookingDetail &&
      access.canViewDomBooking &&
      !access.hasIkest);
  const showBookingSub = showIntlBookingSub || showDomBookingSub;
  const onIntlShipmentPath =
    pathname.startsWith('/d/intl') ||
    pathname.startsWith('/search') ||
    pathname.startsWith('/containerList') ||
    pathname.startsWith('/containerdash');
  const showIntlShipmentSub = access.hasIntlShipment && onIntlShipmentPath;
  const showDomShipmentSub =
    access.hasDomShipment && pathname.startsWith('/d/shipment');
  const onIntlSiPath =
    pathname.startsWith('/shippingInstructions') ||
    pathname.startsWith('/shippingInstructionCompare') ||
    pathname.startsWith('/detentionChargeCalculator');
  const showIntlSiSub = access.hasIntlShippingInstruction && onIntlSiPath;
  const showDomSiSub =
    access.hasDomSI && pathname.startsWith('/d/shipping-instruction');
  const showSiSub = showIntlSiSub || showDomSiSub;
  const onIntlDocPath =
    pathname.startsWith('/billingdoc') ||
    pathname.startsWith('/blanketloi') ||
    pathname.startsWith('/blanketloidetails');
  const showIntlDocSub = access.hasIntlDocument && onIntlDocPath;
  const showDomDocSub =
    access.hasDomDocument && pathname.startsWith('/d/billingdocs');
  const showDocSub = showIntlDocSub || showDomDocSub;
  const showDomAccountStatementSub =
    access.hasDomAccountStatement && pathname.startsWith('/d/account-statement');
  const showDomesticSub =
    showDomShipmentSub ||
    showIntlShipmentSub ||
    showBookingSub ||
    showSiSub ||
    showDocSub ||
    showDomAccountStatementSub;

  const isContainerSearchMainActive =
    access.showContainerSearchInMainNav &&
    (pathname.startsWith('/d/intl') ||
      pathname.startsWith('/search') ||
      pathname.startsWith('/containerList') ||
      pathname === '/');

  return {
    showIntlBookingSub,
    showDomBookingSub,
    showBookingSub,
    showIntlShipmentSub,
    showDomShipmentSub,
    showIntlSiSub,
    showDomSiSub,
    showSiSub,
    showIntlDocSub,
    showDomDocSub,
    showDocSub,
    showDomAccountStatementSub,
    showDomesticSub,
    isBookingActive: showBookingSub,
    isShipmentTrackingActive: showDomShipmentSub || showIntlShipmentSub,
    isContainerSearchMainActive,
    isSiActive: showSiSub,
    isVgmActive: pathname.startsWith('/vgmDetails'),
    isDocActive: showDocSub,
    isSalesViewActive: pathname.startsWith('/salesView'),
    isRepSalesViewActive: pathname.startsWith('/repSalesView'),
    isAccountStatementActive: showDomAccountStatementSub,
    pageHasLegacyHeaderOffset: !pathname.startsWith('/d/'),
    legacyPageTitleKey: getLegacyPageTitleKey(pathname),
  };
}
